import { HelpLinkLibrary } from '@tableau/help-link-library';

import { TelemetryEvents } from '../../common/telemetryEvents/TelemetryEvents';
import { getSessionManagementServiceInstance } from '../../sessionManagement/SessionManagementService';
import { FeatureFlags } from '../../ts/FeatureFlags';
import { Helpers } from '../../ts/helpers/Helpers';
import { Telemetry } from './Telemetry';

const ONLINE_TOKEN_OFFLINE_PAGE_DICT: {[key: string]: string} = {
  'adminusers2': 'csvguidelines.htm',
  'status1': 'service_viewstatus.htm',
  'settings-adsync1': 'groups_globalsync.htm'
};

const TABLEAU_URL = 'https://www.tableau.com';

function search(searchText: string): void {
  searchText = encodeURIComponent(searchText);

  if(FeatureFlags.OnlineAnalyticsPipelineEnabled) {
    Telemetry.sendEvent({
      type: TelemetryEvents.InfoMenuSearchHelp,
      action: 'enterKeypress',
      context: JSON.stringify({searchText: searchText })
    });
  }
  if (getSessionManagementService().offlineHelpEnabled()) {
    // Example: http://localhost/offline_help/en_us/help.htm#search-workbooks
    window.open(offlineHelpRoute('search-') + searchText, '_blank');
  } else {
    // Example: https://www.tableau.com/search/support/workbooks
    window.open(supportUrlPrefix() + '/search/support/' + searchText, '_blank');
  }
}

function getServerHelpUrlByCategory(category?: string): string {
  if (getSessionManagementService().offlineHelpEnabled()) {
    return offlineHelpRoute(ONLINE_TOKEN_OFFLINE_PAGE_DICT[category]);
  } else {
    if (FeatureFlags.helpLinkLibraryIntegration) {
      if (category === undefined || category === 'undefined' || !category) {
        return buildUrl('server-help1');
      }
      let fullCategory = 'server-'.concat(category);
      return buildUrl(fullCategory);
    }
    const lang = helpUrlLanguage();
    // Params must be ordered: edition, lang, platform, version

    const query = Helpers.serialize({
      'edition': getSessionManagementService().helpEdition(),
      'lang': lang,
      'platform': serverPlatform(),
      'version': externalVersion()
    });
    // Example: https://www.tableau.com/en-us/app/server-views1?edition=server&lang=en-us&platform=windows&version=9.0
    return getHelpUrl('server-', lang, query, category);
  }
}

function getHelpUrlByCategory(category?: string, edition?: string, version?: string): string {
  let retval: string = '';
  if (FeatureFlags.helpLinkLibraryIntegration) {
    retval = buildUrl(category);
  } else {
    const lang = helpUrlLanguage();
    // Params must be ordered: edition, lang, platform, version
    const query = Helpers.serialize({
      'edition': edition ? edition : getSessionManagementService().helpEdition(),
      'lang': lang,
      'platform': serverPlatform(),
      'version': version ? version : externalVersion()
    });
    retval = getHelpUrl('', lang, query, category);
}
return retval;
}

function buildUrl(category?: string) {
  let helpLinkObj = new HelpLinkLibrary();
  category = category ?? 'help1';
  const identifiedLang: string = getSessionManagementService().sessionLanguage();
  helpLinkObj.setLanguage(identifiedLang);
  let language: string = helpLinkObj.getLanguage();
  let edition = getSessionManagementService().helpEdition() ?? 'server';
  let version = externalVersion();
  let platform: string = serverPlatform();
  helpLinkObj.setHelpUrl(language, edition, version, category, platform);
  let retval = helpLinkObj.getHelpUrl();
  return retval;
}

function getGeneralSupportUrl(): string {
  if (getSessionManagementService().helpEdition() !== 'online') {
    return this.supportUrlPrefix() + '/support';
  }
  // Open a support request (i.e., Zeus support form)
  return 'https://' + getSessionManagementService().sessionSupportHost() + getSessionManagementService().sessionSupportPath() +
    '?' + Helpers.serialize({
      'uid': getSessionManagementService().sessionAuthUserId(),
      'serverSiteUserId': getSessionManagementService().sessionUserId(),
      'serverSiteId': getSessionManagementService().sessionSiteLuid(),
      'serverInstanceId': getSessionManagementService().sessionServerId()
    });
}

function getQueryParams(lang?: string): string {
  return Helpers.serialize({
    'edition': getSessionManagementService().helpEdition(),
    'lang': lang || helpUrlLanguage(),
    'platform': serverPlatform(),
    'version': externalVersion()
  });
}

function helpUrlLanguage(): string {
  const language = getSessionManagementService().sessionLanguage();
  switch (language) {
    case 'de_DE':
    case 'en_GB':
    case 'en_US':
    case 'es_ES':
    case 'fr_FR':
    case 'it_IT':
    case 'ja_JP':
    case 'ko_KR':
    case 'pt_BR':
    case 'zh_CN':
    case 'zh_TW':
      return language.toLowerCase().replace('_', '-');

    case 'de': return 'de-de';
    case 'en': return 'en-us';
    case 'es': return 'es-es';
    case 'fr': return 'fr-fr';
    case 'it': return 'it-it';
    case 'ja': return 'ja-jp';
    case 'ko': return 'ko-kr';
    case 'pt': return 'pt-br';
    case 'zh': return 'zh-cn';
    default: return 'en-us';
  }
}

// Helper Functions

function getSessionManagementService() {
  return getSessionManagementServiceInstance();
}

function offlineHelpRoute(page?: string): string {
  const lang = helpUrlLanguage().replace('-', '_');
  return 'offline_help/' + lang + '/help.htm' + (page ? '#' + page : '');
}

  // Example: https://www.tableau.com/en-us/app/server-views1?edition=server&lang=en-us&platform=windows&version=9.0
function getHelpUrl(categoryPrefix: string, lang: string, query: string, category?: string): string {
  return `${TABLEAU_URL}/${lang}/app/${categoryPrefix}${(category ? category : 'help1')}?${query}`;
}

function supportUrlPrefix(): string {
  const lang = helpUrlLanguage();
  return TABLEAU_URL + (lang === 'en-us' ? '' : '/' + lang);
}

function serverPlatform(): string {
  const server = getSessionManagementService().sessionServerVersion();
  const platform = server ? server.platform : '';
  return (platform) ? platform.toLowerCase() : 'windows'; // Default to Windows
}

function externalVersion(): string {
  const serverVersionInfo = getSessionManagementService().sessionServerVersion();
  if (!serverVersionInfo) {
    return '0.0';
  }
  const version = serverVersionInfo.externalVersion;
  const intRegExp = /^\d+$/;
  if (intRegExp.test(version.major) && intRegExp.test(version.minor)) {
    return version.major + '.' + version.minor;
  } else {
    return '0.0';
  }
}

export const HelpService = {
  search,
  getServerHelpUrlByCategory,
  getHelpUrlByCategory,
  supportUrlPrefix,
  getGeneralSupportUrl,
  externalVersion,
  getQueryParams,
  helpUrlLanguage,
  buildUrl
};

export type HelpService = typeof HelpService;

